/**
 * ConfirmStep
 *
 * Extracted from LanguageTestModal — Step 4: the analysis outcome.
 *
 * Two outcomes, rendered as what they are: an assessed level (which the modal
 * lets the citizen submit), or a FAILURE with the reason in plain words (which
 * it does not). The failure branch used to be a soft "no result detected" note
 * with Submit still offered — so the test was saved as completed with no level
 * and shown as a green "Completed" (H15).
 */

import type { TFunction } from 'i18next';
import { cn } from '@tonnie/foundation';
import { cefrLevelName } from '@tonnie/heroes';
import { Card, CardContent } from '@tonnie/ui';
import { AlertTriangle, CheckCircle2, XCircle } from '@tonnie/assets';
import { motion } from 'framer-motion';

import type { AnalysisState } from './language-test.types';
import { languageTestFailureMessage } from './language-test.types';

export function ConfirmStep({
  analysis,
  imagePreviewUrl,
  isSubmitted,
  t,
}: {
  analysis: Extract<AnalysisState, { status: 'analyzed' } | { status: 'failed' }>;
  imagePreviewUrl: string | null;
  isSubmitted: boolean;
  t: TFunction;
}) {
  if (isSubmitted) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 200, damping: 15 }}
          className="text-center space-y-4"
        >
          <div className="mx-auto w-20 h-20 rounded-full bg-success/20 flex items-center justify-center">
            <CheckCircle2 className="w-10 h-10 text-success-accessible" />
          </div>
          <h3 className="text-xl font-bold text-foreground">
            {t('checkup.languageTests.submitted')}
          </h3>
          <p className="text-sm text-muted-foreground">
            {t('checkup.languageTests.submittedHint')}
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Uploaded image thumbnail */}
      {imagePreviewUrl && (
        <Card className="border border-border overflow-hidden">
          <div className="bg-muted/30">
            <img
              src={imagePreviewUrl}
              alt={t('checkup.languageTests.alt.uploadedResult')}
              className="w-full object-contain max-h-[200px]"
            />
          </div>
        </Card>
      )}

      {analysis.status === 'failed' ? (
        <Card
          className="border-2 border-destructive/40 bg-destructive/5"
          data-testid="widget-language_test-failed"
          data-failure-reason={analysis.reason}
        >
          <CardContent className="p-6 text-center space-y-3">
            <XCircle className="w-8 h-8 text-destructive-accessible mx-auto" aria-hidden="true" />
            <h4 className="text-base font-semibold text-foreground">
              {t('checkup.languageTests.failed.title')}
            </h4>
            <p className="text-sm text-muted-foreground">
              {languageTestFailureMessage(analysis.reason, t)}
            </p>
            <p className="text-xs text-muted-foreground">
              {t('checkup.languageTests.failed.notCompletedHint')}
            </p>
          </CardContent>
        </Card>
      ) : (
        <AnalyzedResult analysis={analysis} t={t} />
      )}
    </div>
  );
}

function AnalyzedResult({
  analysis,
  t,
}: {
  analysis: Extract<AnalysisState, { status: 'analyzed' }>;
  t: TFunction;
}) {
  const { cefrResult, level } = analysis;
  const description = cefrLevelName(level, t);
  const isHighConfidence = analysis.confidence >= 0.7;

  return (
    <Card
      data-testid="widget-language_test-analyzed"
      data-cefr-level={level}
      className={cn(
        'border-2',
        isHighConfidence
          ? 'border-success/40 bg-linear-to-br from-success/10 to-background'
          : 'border-tone-amber-line bg-linear-to-br from-amber-500/10 to-background'
      )}
    >
      <CardContent className="p-6 text-center space-y-3">
        <div className="flex items-center justify-center gap-2">
          {isHighConfidence ? (
            <CheckCircle2 className="w-5 h-5 text-success-accessible" />
          ) : (
            <AlertTriangle className="w-5 h-5 text-tone-amber" />
          )}
          <span className="text-sm font-medium text-muted-foreground">
            {isHighConfidence
              ? t('checkup.languageTests.resultDetected')
              : t('checkup.languageTests.resultLowConfidence')}
          </span>
        </div>
        <div className="flex items-center justify-center gap-3">
          <span className="text-4xl font-black text-foreground">{level}</span>
          {description && (
            <span className="text-lg text-muted-foreground font-medium">&mdash; {description}</span>
          )}
        </div>
        {/* Breakdown if available */}
        {(cefrResult.speaking || cefrResult.reading || cefrResult.listening) && (
          <div className="flex items-center justify-center gap-6 pt-2 border-t border-border/50 mt-2">
            {cefrResult.speaking && (
              <div className="text-center">
                <span className="text-xs text-muted-foreground block">
                  {t('checkup.languageTests.speaking')}
                </span>
                <span className="text-sm font-bold text-foreground">{cefrResult.speaking}</span>
              </div>
            )}
            {cefrResult.reading && (
              <div className="text-center">
                <span className="text-xs text-muted-foreground block">
                  {t('checkup.languageTests.reading')}
                </span>
                <span className="text-sm font-bold text-foreground">{cefrResult.reading}</span>
              </div>
            )}
            {cefrResult.listening && (
              <div className="text-center">
                <span className="text-xs text-muted-foreground block">
                  {t('checkup.languageTests.listening')}
                </span>
                <span className="text-sm font-bold text-foreground">{cefrResult.listening}</span>
              </div>
            )}
          </div>
        )}
        {!isHighConfidence && (
          <p className="text-xs text-tone-amber">{t('checkup.languageTests.lowConfidenceHint')}</p>
        )}
      </CardContent>
    </Card>
  );
}
